<template>
  <div class="container-lg">
    <div>
      <div :class="s.title">
        <p class="fs-3 fw-bold mb-4">
          <i class="fa-solid fa-burger-soda" />
          メニュー商品管理
        </p>
      </div>
      <div class="row">
        <div class="col-9" style="max-height: 130vh; overflow-y: auto;" ref="scrollContainer">
          <Loader :flag="flag.isLoading">
            <div v-if="!items?.length" class="mt-3">
              <p class="fs-6">※商品が登録されていません。新規登録してください。</p>
            </div>
            <draggable
              v-else
              v-model="items"
              item-key="id"
              :disabled="!!editId"
              @end="onDragEnd"
              class="list-group list-group-checkable d-grid gap-2 border-0"
              ghost-class="ghost"
              handle=".drag-handle"
              :scroll="scrollContainer"
              :scrollSensitivity="50"
              :scrollSpeed="20"
              :forceFallback="true"
              :bubbleScroll="true">
              <template #item="{element: item}">
                <li
                  :class="[s.item, editId === item.id ? s.edit : '']"
                  class="list-group-item rounded-3 py-3">
                <div
                  v-if="editId !== item.id"
                  class="d-flex align-items-center justify-content-between">
                  <div class="d-flex align-items-center">
                    <div class="drag-handle me-2" style="cursor: grab;">
                      <i class="fa-solid fa-grip-vertical" style="font-size: 1.2rem; color: #999;"></i>
                    </div>
                    <div style="margin-right: 10px;">
                      <img v-if="item.image_url" :src="item.image_url" alt="Current image" :class="s.imagePreview">
                      <img v-else src="/img/menu/no_image.png" alt="No image" :class="s.imagePreview">
                    </div>
                    <div>
                      {{ item.label || '名前未設定' }}
                      <span class="d-inline opacity-50">{{ item.name }}</span>
                      <span v-if="item.is_recommended" class="badge bg-danger ms-2">おすすめ</span>
                      <span v-if="item.parent_item_id" class="badge bg-success ms-2">サブメニュー</span>
                      <span class="d-block small">
                        <span v-if="item.price === 0">無料</span>
                        <span v-else>
                        {{ Math.round(item.price / 1.1) }}円
                        (税込{{ Math.round(item.price) }}円)</span>
                        </span>
                    </div>
                  </div>
                  <div class="d-flex align-items-center">
                    <div class="form-check me-2">
                      <input
                        class="form-check-input"
                        type="checkbox"
                        :id="'outOfStock-' + item.id"
                        :checked="item.flag === 2"
                        @change="toggleOutOfStock(item)">
                      <label class="form-check-label" :for="'outOfStock-' + item.id">
                        売切
                      </label>
                    </div>
                    <button
                      @click="startEdit(item)"
                      class="btn btn-outline-success btn-sm">編集</button>
                  </div>
                </div>
                <!-- 通常メニューの編集フォーム -->
                <div
                  :class="s.form"
                  v-else-if="!isEditingVariant">
                  <div>
                    <label for="">ステータス</label>
                    <select
                      class="form-control form-control-sm"
                      name="flag"
                      id="flag"
                      v-model="updateData.flag">
                      <option
                      v-for="(flag) of saleFlag"
                      :key="flag.label"
                      :value="flag.value">{{ flag.label }}</option>
                    </select>
                  </div>
                  <div class="mt-3">
                    <label for="">商品名</label>
                    <input
                      type="text"
                      class="form-control form-control-sm"
                      v-model="updateData.label">
                  </div>
                  <div class="mt-3">
                    <label for="">商品名(管理)</label>
                    <input
                      type="text"
                      class="form-control form-control-sm"
                      v-model="updateData.name">
                  </div>
                  <div class="mt-3">
                    <label for="">ジャンル</label>
                    <select
                      class="form-control form-control-sm"
                      name="menusId"
                      id="menusId"
                      v-model="updateData.menu_id">
                      <option :value="null">選択してください</option>
                      <option
                        v-for="menu in getMenuOptions"
                        :key="menu.id"
                        :value="menu.id">
                        {{ menu.label }}
                      </option>
                    </select>
                  </div>
                  <div class="mt-3">
                    <label for="">価格(税込)</label>
                    <input
                      type="text"
                      class="form-control form-control-sm"
                      v-model="updateData.price"
                      :disabled="updateData.menu_id === 999">
                  </div>
                  <div class="mt-3">
                    <label for="newImage" class="ms-1">商品画像</label>
                    <div class="d-flex align-items-center">
                      <input
                        type="file"
                        class="form-control form-control-sm"
                        id="newImageFile"
                        @change="handleNewImageFileChange"
                        accept="image/*">
                      <button
                        class="btn btn-outline-danger btn-sm ms-2"
                        @click="clearImage(item)">削除</button>
                    </div>
                    <div v-if="!newImagePreviewUrl" class="mt-2">
                      <img v-if="item.image_url" :src="item.image_url" alt="Current image" :class="s.imagePreview">
                      <img v-else src="/img/menu/no_image.png" alt="No image" :class="s.imagePreview">
                    </div>
                    <div v-if="newImagePreviewUrl" class="mt-2">
                      <img :src="newImagePreviewUrl" alt="New image preview" style="max-width: 100px; max-height: 100px; object-fit: cover;">
                    </div>
                  </div>
                  <div class="mt-3">
                    <label for="">商品説明</label>
                    <input
                      type="text"
                      class="form-control form-control-sm"
                      v-model="updateData.description">
                  </div>
                  <div class="mt-3 text-start">
                    <div class="form-check">
                      <input
                        class="form-check-input"
                        type="checkbox"
                        id="isRecommendedUpdate"
                        v-model="updateData.is_recommended">
                      <label class="form-check-label" for="isRecommendedUpdate">
                        おすすめ商品として表示
                      </label>
                    </div>
                  </div>
                  <div class="d-flex mt-3">
                    <button
                      class="btn btn-primary btn-sm"
                      :disabled="!updateData.flag || !updateData.name || !updateData.label || updateData.price === null"
                      @click="regist">更新</button>
                    <button
                      @click="cancelEdit()"
                      class="btn btn-outline-danger btn-sm ms-2">キャンセル</button>
                  </div>
                </div>
                <!-- サブメニューの編集フォーム -->
                <div
                  :class="s.form"
                  v-else-if="isEditingVariant">
                  <div class="mt-3">
                    <label for="">親商品</label>
                    <select
                      class="form-control form-control-sm"
                      v-model="updateData.parent_item_id">
                      <option :value="null">選択してください</option>
                      <option
                        v-for="parentItem in parentItems"
                        :key="parentItem.id"
                        :value="parentItem.id">
                        {{ parentItem.label }}
                      </option>
                    </select>
                  </div>
                  <div class="mt-3">
                    <label for="">バリエーション名</label>
                    <input
                      type="text"
                      class="form-control form-control-sm"
                      v-model="updateData.label">
                  </div>
                  <div class="mt-3">
                    <label for="">バリエーション名(管理)</label>
                    <input
                      type="text"
                      class="form-control form-control-sm"
                      v-model="updateData.name">
                  </div>
                  <div class="mt-3">
                    <label for="">価格(税込)</label>
                    <input
                      type="text"
                      class="form-control form-control-sm"
                      v-model="updateData.price">
                  </div>
                  <div class="mt-3 text-start">
                    <div class="form-check">
                      <input
                        class="form-check-input"
                        type="checkbox"
                        id="isDefaultUpdate"
                        v-model="updateData.is_default">
                      <label class="form-check-label" for="isDefaultUpdate">
                        デフォルトのバリエーション
                      </label>
                    </div>
                  </div>
                  <div class="d-flex mt-3">
                    <button
                      class="btn btn-primary btn-sm"
                      :disabled="!updateData.parent_item_id || !updateData.name || !updateData.label || updateData.price === null"
                      @click="regist">更新</button>
                    <button
                      @click="cancelEdit()"
                      class="btn btn-outline-danger btn-sm ms-2">キャンセル</button>
                  </div>
                </div>
                </li>
              </template>
            </draggable>
          </Loader>
        </div>
        <div class="col-3 cards">
        <div class="card mb-4">
          <div class="card-header"><i class="fa-regular fa-magnifying-glass me-2"/>検索</div>
            <div class="card-body" :class="s.form">
              <div class="text-start">
                <label for="" class="ms-1">キーワード</label>
                <input
                  type="text"
                  class="form-control form-control-sm"
                  v-model="searchParams.keyword"
                  placeholder="商品名を入力">
              </div>
            <div class="text-start mt-2">
              <label for="" class="ms-1">ステータス</label>
              <select
                class="form-control form-control-sm"
                name="flag"
                id="flag"
                v-model="searchParams.flag">
                <option :value="null">選択してください</option>
                <option
                v-for="(flag) of saleFlag"
                :key="flag.label"
                :value="flag.value">{{ flag.label }}</option>
              </select>
            </div>
            <div class="text-start mt-2">
              <label for="" class="ms-1">ジャンル</label>
                <select
                class="form-control form-control-sm"
                name="menusId"
                id="menusId"
                  v-model="searchParams.menu_id">
                  <option :value="null">選択してください</option>
                  <option
                    v-for="menu in getMenuOptions"
                    :key="menu.id"
                    :value="menu.id">
                    {{ menu.label }}
                  </option>
                </select>
              </div>
            <div class="text-start mt-2">
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="checkbox"
                  id="isRecommendedFilter"
                  v-model="searchParams.is_recommended">
                <label class="form-check-label" for="isRecommendedFilter">
                  おすすめ商品のみ表示
                </label>
              </div>
            </div>
          </div>
        </div>
        <div class="card">
          <div class="card-header"><i class="fa-regular fa-pen-to-square me-2"/>新規登録</div>
            <div class="card-body" :class="s.form">
              <div class="mt-3 text-start">
                <label for="email" class="ms-1">商品名</label>
                <input
                  type="text"
                  class="form-control form-control-sm"
                  v-model="label"
                  :disabled="editId"
                  placeholder="コーラ">
              </div>
              <div class="mt-3 text-start">
                <label for="email" class="ms-1">商品名(管理)</label>
                <input
                  type="text"
                  class="form-control form-control-sm"
                  v-model="name"
                  :disabled="editId"
                  placeholder="cola">
              </div>
              <div class="mt-3 text-start">
                <label for="" class="ms-1">ジャンル</label>
                <select
                class="form-control form-control-sm"
                name="menusId"
                id="menusId"
                  v-model="menu_id">
                  <option :value="null">選択してください</option>
                  <option
                    v-for="menu in getMenuOptions"
                    :key="menu.id"
                    :value="menu.id">
                    {{ menu.label }}
                  </option>
                </select>
              </div>
              <div class="mt-3 text-start">
                <label for="email" class="ms-1">価格(税込)</label>
                <input
                  type="text"
                  class="form-control form-control-sm"
                  v-model="price"
                  :disabled="editId || menu_id === 999"
                  placeholder="108">
              </div>
              <div class="mt-3 text-start">
                <label for="newImage" class="ms-1">商品画像</label>
                <input
                  type="file"
                  class="form-control form-control-sm"
                  id="newImageFile"
                  @change="handleNewImageFileChange"
                  accept="image/*"
                  :disabled="editId">
                  <div v-if="newImagePreviewUrl && !editId" class="mt-2">
                    <img :src="newImagePreviewUrl" alt="New image preview" :class="s.imagePreview">
                  </div>
              </div>
              <div class="mt-3 text-start">
                <label for="email" class="ms-1">商品説明</label>
                <input
                  type="text"
                  class="form-control form-control-sm"
                  v-model="description"
                  :disabled="editId">
              </div>
              <div class="mt-3 text-start">
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    id="isRecommendedCreate"
                    v-model="is_recommended"
                    :disabled="editId">
                  <label class="form-check-label" for="isRecommendedCreate">
                    おすすめ商品として表示
                  </label>
                </div>
              </div>
              <button
                :disabled="!label || !name || price === null"
                @click="regist"
                class="btn btn-primary w-100 py-2 mt-3">登録</button>
            </div>
          </div>
        <div class="card mt-4">
          <div class="card-header"><i class="fa-regular fa-pen-to-square me-2"/>サブメニュー新規登録</div>
            <div class="card-body" :class="s.form">
              <div class="mt-3 text-start">
                <label for="" class="ms-1">親商品</label>
                <select
                  class="form-control form-control-sm"
                  v-model="variant_parent_id"
                  :disabled="editId">
                  <option :value="null">選択してください</option>
                  <option
                    v-for="item in parentItems"
                    :key="item.id"
                    :value="item.id">
                    {{ item.label }}
                  </option>
                </select>
              </div>
              <div class="mt-3 text-start">
                <label for="" class="ms-1">バリエーション名</label>
                <input
                  type="text"
                  class="form-control form-control-sm"
                  v-model="variant_label"
                  placeholder="大盛り"
                  :disabled="editId">
              </div>
              <div class="mt-3 text-start">
                <label for="" class="ms-1">バリエーション名(管理)</label>
                <input
                  type="text"
                  class="form-control form-control-sm"
                  v-model="variant_name"
                  placeholder="large"
                  :disabled="editId">
              </div>
              <div class="mt-3 text-start">
                <label for="" class="ms-1">価格(税込)</label>
                <input
                  type="text"
                  class="form-control form-control-sm"
                  v-model="variant_price"
                  placeholder="108"
                  :disabled="editId">
              </div>
              <div class="mt-3 text-start">
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    id="isDefaultVariant"
                    v-model="variant_is_default"
                    :disabled="editId">
                  <label class="form-check-label" for="isDefaultVariant">
                    デフォルトのバリエーション
                  </label>
                </div>
              </div>
              <button
                :disabled="!variant_parent_id || !variant_label || !variant_name || variant_price === null || editId"
                @click="registVariant"
                class="btn btn-success w-100 py-2 mt-3">サブメニュー登録</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import {
  Loader,
} from '@/components/parts';
import draggable from 'vuedraggable';

export default {
  name: 'admin-item',
  components: {
    Loader,
    draggable,
  },
  data() {
    return {
      scrollContainer: null,
      flag: {
        isLoading: true,
        changeKeyword: false,
        clearImage: false,
      },
      validationFlags: {
        mail: false,
      },
      invalids: {
        mail: true,
      },
      // 有効期限切れでリダイレクトされた
      isExpired: false,
      items: [],
      menus: [],
      searchParams: {
        flag: null,
        keyword: null,
        menu_id: null,
        is_recommended: false,
      },
      label: null,
      name: null,
      price: null,
      description: null,
      menu_id: null,
      is_recommended: false,
      newImageFile: null,
      variant_parent_id: null,
      variant_label: null,
      variant_name: null,
      variant_price: null,
      variant_is_default: false,
      updateData: {
        flag: null,
        label: null,
        name: null,
        price: null,
        description: null,
        menu_id: null,
        is_recommended: false,
        parent_item_id: null,
        is_default: false,
      },
      saleFlag: [
        {
          name: 'flag',
          label: '販売中',
          value: 1,
          hover: false,
        },
        {
          name: 'flag',
          label: '停止中',
          value: 0,
          hover: false,
        },
        {
          name: 'flag',
          label: '売り切れ',
          value: 2,
          hover: false,
        },
        {
          name: 'flag',
          label: 'サービス',
          value: 998,
          hover: false,
        },
        {
          name: 'flag',
          label: 'スタッフ呼び出し',
          value: 999,
          hover: false,
        },
      ],
      editId: null,
      targetColumns: ['name', 'label'],
      service_item_id: 999, // サービス用のID
    };
  },
  created() {
    this.getMenuItems();
    this.getMenus();
  },
  mounted() {
    this.scrollContainer = this.$refs.scrollContainer;
  },
  computed: {
    ...mapState(['user']),
    submitFlag() {
      // 無効フラグが全てfalseならばtrueを返却
      return !this.invalids.mail;
    },
    getMenuOptions() {
      return this.menus.map(type => ({
        id: type.id,
        label: type.label,
      }));
    },
    newImagePreviewUrl() {
      return this.newImageFile ? URL.createObjectURL(this.newImageFile) : null;
    },
    isEditingVariant() {
      // 編集中のアイテムがサブメニュー（バリエーション）かどうか
      if (!this.editId) return false;
      const editingItem = this.items.find((item) => item.id === this.editId);
      return editingItem && editingItem.parent_item_id !== null;
    },
    parentItems() {
      // 親商品のみを返す（parent_item_idがnullのもの）
      return this.items.filter((item) => !item.parent_item_id);
    },
  },
  watch: {
    item: {
      handler() {
        this.getMenuItems();
      },
      deep: true,
    },
    searchParams: {
      handler() {
        if (!this.editId) {
          this.getMenuItems();
        }
      },
      deep: true,
    },
    menu_id(newValue) {
      if (newValue === this.service_item_id) {
        this.price = 0;
      } else {
        this.price = null;
      }
    },
    'updateData.menu_id': function handleUpdateDataMenuIdChange(newValue) {
      if (newValue === this.service_item_id) {
        this.updateData.price = 0;
      } else {
        this.updateData.price = this.editId ? this.items.find((item) => item.id === this.editId)?.price : null; // 元の値に戻す
      }
    },
  },
  methods: {
    /** ローディング表示 */
    showLoading() {
      const args = { modalName: 'modalLoadingBallScaleRippleMultiple' };
      this.$store.dispatch('modal/loadings/showModal', args, { root: true });
    },

    /** ローディング終了 */
    hideLoading() {
      this.$store.dispatch('modal/loadings/hideModal', null, { root: true });
    },

    getMenuItems() {
      if (!this.user.id) return;
      this.flag.isLoading = true;

      const params = {
        user_id: this.user.id,
      };

      if (this.searchParams.flag) params.flags = [this.searchParams.flag];
      if (this.searchParams.keyword) {
        params.targetColumn = this.targetColumns;
        params.keyword = [this.searchParams.keyword];
      }
      if (this.searchParams.menu_id) params.menuId = this.searchParams.menu_id;
      if (this.searchParams.is_recommended) params.isRecommended = true;

      this.axios({
        method: 'get',
        url: 'v1/menuitem/get/list',
        params,
      })
        .then((response) => {
          this.items = response.data.list.data;
        })
        .catch((error) => {
          console.log(error);
          alert('エラーが発生しました。');
        })
        .finally(() => {
          this.flag.isLoading = false;
        });
    },

    getMenus() {
      const params = {
        user_id: this.user.id,
      };
      this.axios({
        method: 'GET',
        url: '/v1/menu/get/list',
        params,
      })
        .then((response) => {
          const res = response.data;
          this.menus = res.list.data;
        })
        .catch((error) => {
          if (error.response) console.log(error.response);
          else console.log(error);
        });
    },

    /** 無効フラグの更新 */
    invalidsCheck(name, bool) {
      this.invalids[name] = bool;
    },

    init() {
      this.editId = null;
      this.updateData.flag = null;
      this.updateData.label = null;
      this.updateData.name = null;
      this.updateData.price = null;
      this.updateData.description = null;
      this.updateData.menu_id = null;
      this.updateData.is_recommended = false;
      this.updateData.parent_item_id = null;
      this.updateData.is_default = false;
      this.newImageFile = null;
      this.label = null;
      this.name = null;
      this.price = null;
      this.description = null;
      this.menu_id = null;
      this.is_recommended = false;
      this.variant_parent_id = null;
      this.variant_label = null;
      this.variant_name = null;
      this.variant_price = null;
      this.variant_is_default = false;
    },

    startEdit(item) {
      this.init();
      this.editId = item.id;

      // サブメニュー（バリエーション）の場合
      if (item.parent_item_id) {
        this.updateData.parent_item_id = item.parent_item_id;
        this.updateData.label = item.label;
        this.updateData.name = item.name;
        this.updateData.price = item.price;
        this.updateData.is_default = item.is_default || false;
      } else {
        // 通常の商品の場合
        this.updateData.flag = item.flag;
        this.updateData.label = item.label;
        this.updateData.name = item.name;
        this.updateData.price = item.price;
        this.updateData.description = item.description;
        this.updateData.menu_id = item.menu_id;
        this.updateData.is_recommended = item.is_recommended || false;
      }
      this.newImageFile = null;
    },

    cancelEdit() {
      this.init();
      this.flag.clearImage = false;
    },

    /** サブミット */
    regist(e) {
      e.preventDefault();
      this.showLoading();

      this.flag.isLoading = true;


      const formData = new FormData();

      const endpoint = this.editId ? 'update' : 'create';

      // サブメニュー編集の場合
      if (this.editId && this.isEditingVariant) {
        formData.append('flag', '1');
        formData.append('label', this.updateData.label);
        formData.append('name', this.updateData.name);
        formData.append('price', this.updateData.price);
        formData.append('user_id', this.user.id);
        formData.append('id', this.editId);

        // 親商品のmenu_idを取得
        const parentItem = this.parentItems.find((item) => item.id === this.updateData.parent_item_id);
        if (parentItem) {
          formData.append('menu_id', parentItem.menu_id);
        }

        formData.append('parent_item_id', this.updateData.parent_item_id);
        formData.append('variant_type', 'variant');
        formData.append('variant_value', this.updateData.label);
        formData.append('is_default', this.updateData.is_default ? 'true' : 'false');
      } else {
        // 通常メニューの場合
        const flagValue = this.editId ? this.updateData.flag : '1';
        const labelValue = this.editId ? this.updateData.label : this.label;
        const nameValue = this.editId ? this.updateData.name : this.name;
        const priceValue = this.editId ? this.updateData.price : this.price;
        const descriptionValue = this.editId ? this.updateData.description : this.description;
        const menuIdValue = this.editId ? this.updateData.menu_id : this.menu_id;
        const isRecommendedValue = this.editId ? this.updateData.is_recommended : this.is_recommended;
        if (flagValue) formData.append('flag', flagValue);
        if (labelValue) formData.append('label', labelValue);
        if (nameValue) formData.append('name', nameValue);
        if (priceValue !== null) {
          formData.append('price', priceValue);
        }
        if (descriptionValue) formData.append('description', descriptionValue);
        if (menuIdValue) formData.append('menu_id', menuIdValue);
        formData.append('is_recommended', isRecommendedValue ? 'true' : 'false');
        formData.append('user_id', this.user.id);

        if (endpoint === 'create') {
          formData.set('flag', '1');
        }
        if (endpoint === 'update') formData.set('id', this.editId);
        if (this.newImageFile) {
          formData.append('image', this.newImageFile);
        } else if (this.flag.clearImage) {
          formData.append('clear_image', '1');
        }
      }
      this.axios({
        method: 'POST',
        url: `/v1/menuitem/${endpoint}`,
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
        .then((response) => {
          const res = response.data;
          if (endpoint === 'create' && res.exists) {
            alert('商品名(管理)が重複しています。');
          } else if (endpoint === 'create') {
            alert('商品を登録しました。');
            this.init();
          }
          if (endpoint === 'update') {
            if (this.isEditingVariant) {
              alert('サブメニューを更新しました。');
            } else {
              alert('商品情報を更新しました。');
            }
            this.init();
          }
        })
        .catch((error) => {
          if (error.response) console.log(error.response.data);
          else console.log(error);
          if (error.response.data.status === 422) {
            alert('不正な画像形式です。');
          } else {
            alert('エラーが発生しました。');
          }
        })
        .finally(() => {
          this.flag.isLoading = false;
          this.flag.clearImage = false;
          this.getMenuItems();
          this.hideLoading();
        });
    },
    toggleOutOfStock(item) {
      this.showLoading();
      const newFlag = item.flag === 2 ? 1 : 2;
      const formData = new FormData();
      formData.append('id', item.id);
      formData.append('flag', newFlag);
      formData.append('user_id', this.user.id);
      formData.append('name', item.name);

      this.axios({
        method: 'POST',
        url: '/v1/menuitem/update',
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
        .then(() => {
          this.getMenuItems();
        })
        .catch((error) => {
          console.log(error);
          alert('ステータスの更新に失敗しました。');
        })
        .finally(() => {
          this.hideLoading();
        });
    },
    handleNewImageFileChange(event) {
      const file = event.target.files[0];
      if (file) {
        this.newImageFile = file;
      }
    },
    clearImage(item) {
      this.newImageFile = null; // 新しい画像をクリア
      this.updateData.image_url = null; // 現在の画像をクリア
      item.image_url = null; // アイテムの画像URLをクリア
      console.log('画像をクリアしました');
      this.flag.clearImage = true;
    },

    onDragEnd() {
      // ドラッグ終了時に並び順を更新
      this.showLoading();

      const reorderedItems = this.items.map((item, index) => ({
        id: item.id,
        sort_id: (index + 1) * 10,
      }));

      this.axios({
        method: 'post',
        url: '/v1/menuitem/reorder',
        data: {
          items: reorderedItems,
          user_id: this.user.id,
        },
      })
        .then(() => {
          // 並び順更新成功
          this.getMenuItems();
        })
        .catch((error) => {
          console.log(error);
          alert('並び順の更新に失敗しました。');
          // 失敗したらリロード
          this.getMenuItems();
        })
        .finally(() => {
          this.hideLoading();
        });
    },

    /** サブメニュー（バリエーション）登録 */
    registVariant(e) {
      e.preventDefault();
      this.showLoading();

      this.flag.isLoading = true;

      // 親商品の情報を取得
      const parentItem = this.items.find((item) => item.id === this.variant_parent_id);
      if (!parentItem) {
        alert('親商品が見つかりません。');
        this.hideLoading();
        return;
      }

      const formData = new FormData();
      formData.append('flag', '1');
      formData.append('label', this.variant_label);
      formData.append('name', this.variant_name);
      formData.append('price', this.variant_price);
      formData.append('menu_id', parentItem.menu_id); // 親商品と同じメニューID
      formData.append('parent_item_id', this.variant_parent_id);
      formData.append('variant_type', 'variant');
      formData.append('variant_value', this.variant_label);
      formData.append('is_default', this.variant_is_default ? 'true' : 'false');
      formData.append('user_id', this.user.id);

      this.axios({
        method: 'POST',
        url: '/v1/menuitem/create',
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
        .then((response) => {
          const res = response.data;
          if (res.exists) {
            alert('商品名(管理)が重複しています。');
          } else {
            alert('サブメニューを登録しました。');
            this.init();
          }
        })
        .catch((error) => {
          if (error.response) console.log(error.response.data);
          else console.log(error);
          alert('エラーが発生しました。');
        })
        .finally(() => {
          this.flag.isLoading = false;
          this.getMenuItems();
          this.hideLoading();
        });
    },

  },
};

</script>

<style lang="scss" module="s">
.wrap {
  display: flex;
  justify-content: center;
  min-height: 100vh; /* 画面以上は内容に合わせて伸びる */
  height: auto;
}

.title {
  text-align: center;
  color: rgba(152, 121, 89, 1);
}

.form {
  text-align: center;
}

.btn {
  display: inline-block;
  border-radius: 22px;
  border: none;
  background-color: #666;
  font-weight: bold;
  color: #fff;
  appearance: none;
  padding: 8px 25px;
  font-size: 16px;
}
.form {
  label {
    color: var(--label-secondary);
    font-size: 14px;
    font-weight: 700;
  }
}
.item {
  transition: all .5s;
  max-height: 80px;
  overflow: hidden;
  &.edit {
    max-height: 1000px;
  }
}

.message {
  font-size: 12px;
  text-align: center;
}

.imagePreview {
  max-width: 80px;
  max-height: 50px;
  object-fit: fill;
}

.input {
  margin: 0 auto;
  width: 400px;
  max-width: 100%;
  input {
    width: 100%;
    padding: 13px;
    border-radius: 8px;
    border: none;
    background-color: var(--gray-sub);
    outline: none;
  }
}
.comment {
  font-size: 12px;
  color: var(--red);
}
</style>

<style lang="scss" scoped>
.list-group-item {
  border: 2px solid rgba(152, 121, 89, 1);
  border-top-width: 1px !important;
}
.btn-outline-danger {
  color: #dc3545;
  border-color: #dc3545;
  font-size: 10px;
  white-space: nowrap;
}

.btn-outline-danger:hover {
  background-color: #dc3545;
  color: #fff;
}
.card {
  border: 2px solid rgba(152, 121, 89, 1);
}

.ghost {
  opacity: 0.5;
  background: #f0f0f0;
}
</style>
